import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import { useGlobalSearchQuery } from "@graphql/global-search/globalSearch.g";
import { useDebounce } from "@uidotdev/usehooks";
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { Icon } from "@design-system/Icon/Icon";
import { Input } from "@design-system/Input/Input";
import { Resource } from "@design-system/Resource/Resource";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { Text } from "@design-system/Text/Text";
import { User } from "@design-system/User/User";

import { routes } from "@pages/routes";

import { GlobalSearchContext } from "./GlobalSearchContext";
import { GlobalSearchOption } from "./GlobalSearchOption";

import classes from "./GlobalSearch.module.css";

const SEARCH_DEBOUNCE_TIME = 300;

type SearchScope = "this-client" | "all-clients";

type SelectValue = string;

type Props = {
  onSelect?: (id: SelectValue) => void;
};

export const GlobalSearch = ({ onSelect }: Props) => {
  const navigate = useNavigate();
  const { clientId: routeClientId } = useParams();
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [scope, setScope] = useState<SearchScope>(
    routeClientId ? "this-client" : "all-clients",
  );

  // Entering a client defaults to that client; leaving forces all-clients.
  useEffect(() => {
    setScope(routeClientId ? "this-client" : "all-clients");
  }, [routeClientId]);

  const isClientScoped = scope === "this-client" && !!routeClientId;
  const scopedClientId = isClientScoped ? routeClientId : undefined;

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  const globalSearchQuery = useGlobalSearchQuery(
    {
      search: debouncedSearch,
      clientId: scopedClientId,
      isClientScoped,
    },
    { enabled: !!debouncedSearch },
  );

  const results = useMemo(() => {
    const clients = globalSearchQuery.data?.clients?.edges.map(
      (edge) => edge?.node,
    );
    const users = globalSearchQuery.data?.users?.edges.map(
      (edge) => edge?.node,
    );
    const employees = globalSearchQuery.data?.clientEmployees?.edges.map(
      (edge) => edge?.node,
    );
    const applications =
      globalSearchQuery.data?.applicationResources?.edges.map(
        (edge) => edge?.node,
      );
    const catalogResources = globalSearchQuery.data?.resources?.edges.map(
      (edge) => edge?.node,
    );

    return {
      clients,
      users,
      employees,
      applications,
      catalogResources,
    };
  }, [globalSearchQuery.data]);

  const isClientsFound =
    !isClientScoped && results.clients && results.clients.length > 0;
  const isUsersFound =
    !isClientScoped && results.users && results.users.length > 0;
  const isEmployeesFound = results.employees && results.employees.length > 0;
  // Catalogue is MSP-wide; never show under "This client".
  const isCatalogFound =
    !isClientScoped &&
    !!results.catalogResources &&
    results.catalogResources.length > 0;

  const employeesGroupedByTenant = useMemo(() => {
    if (!results.employees) return [];

    const grouped: Record<
      string,
      {
        clientId: string;
        clientName: string;
        employees: {
          employeeId: string;
          name: string;
          email: string;
        }[];
      }
    > = {};

    results.employees.forEach((employee) => {
      if (!employee) return;

      const clientId = employee.client?.id;
      const clientName = employee.client?.name;

      if (!clientId) return;

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName: clientName || "",
          employees: [],
        };
      }

      grouped[clientId].employees.push({
        employeeId: employee.id || "",
        name: employee.name || "",
        email: employee.email || "",
      });
    });

    return Object.values(grouped).sort((a, b) =>
      a.clientName.localeCompare(b.clientName),
    );
  }, [results.employees]);

  const applicationsGroupedByTenant = useMemo(() => {
    if (!results.applications) return [];

    const grouped: Record<
      string,
      {
        clientId: string;
        clientName: string;
        apps: {
          clientAppId: string;
          applicationId: string;
          productName: string;
          vendorName: string;
          productDomainName: string;
          vendorDomainName: string;
          signedLogoUrl: string;
          hasLogo: boolean;
        }[];
      }
    > = {};

    results.applications.forEach((application) => {
      application?.applications?.edges.forEach((clientApp) => {
        const clientId = clientApp?.node?.client?.id;
        const clientName = clientApp?.node?.client?.name;

        if (!clientId) return;

        if (!grouped[clientId]) {
          grouped[clientId] = {
            clientId,
            clientName: clientName || "",
            apps: [],
          };
        }

        grouped[clientId].apps.push({
          clientAppId: clientApp?.node?.id || "",
          applicationId: application.id,
          productName: application.productName || "",
          vendorName: application.vendorName || "",
          productDomainName: application.productDomainName || "",
          vendorDomainName: application.vendorDomainName || "",
          signedLogoUrl: application.publicResource?.signedLogoUrl || "",
          hasLogo: !!application.publicResource?.signedLogoUrl,
        });
      });
    });

    return Object.values(grouped).sort((a, b) =>
      a.clientName.localeCompare(b.clientName),
    );
  }, [results.applications]);

  const isApplicationsFound = applicationsGroupedByTenant.length > 0;

  const isEmpty =
    !isClientsFound &&
    !isUsersFound &&
    !isEmployeesFound &&
    !isApplicationsFound &&
    !isCatalogFound;

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    elements: {
      reference,
    },
    middleware: [
      flip({
        padding: 12,
      }),
      offset({ mainAxis: 12 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  // virtual: keep focus in the input while arrow keys move the highlight
  // (combobox pattern). Enter activates the highlighted suggestion below.
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
    focusItemOnHover: false,
  });

  const focus = useFocus(context);
  const role = useRole(context, { role: "listbox" });
  // getReferenceProps is on the input, so Floating UI treats the input as the
  // reference. Scope controls are siblings of that input inside the search
  // chrome — guard so toggling scope does not count as an outside dismiss.
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      const target = event.target;
      if (target instanceof Node && reference?.contains(target)) {
        return false;
      }
      setSearch("");
      return true;
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNavigation, focus, dismiss, role],
  );

  const closeSuggestions = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setActiveIndex(null);
    setSelectedIndex(null);
  }, []);

  const handleSelect = useCallback(
    (id: string, index: number | null) => {
      onSelect?.(id);
      setSelectedIndex(index);
      setIsOpen(false);
      setSearch("");
      setActiveIndex(null);
    },
    [onSelect],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setSearch(next);
    setActiveIndex(null);
    if (next) {
      setIsOpen(true);
    }
  };

  // Keep input focus (and thus the open results) when clicking scope controls.
  const handleScopeMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleScopeChange = (next: SearchScope) => {
    setScope(next);
    if (search) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // Prevent space key from being captured by list navigation when typing
      if (event.key === " ") {
        event.stopPropagation();
      }

      // virtual list nav does not auto-activate; Enter clicks the highlight.
      if (event.key === "Enter" && activeIndex != null) {
        const activeItem = elementsRef.current[activeIndex];
        if (activeItem) {
          event.preventDefault();
          activeItem.click();
        }
      }
    },
    [activeIndex],
  );

  const handleClientClick = useCallback(
    (clientId?: string) => {
      if (!clientId) return;

      navigate(
        routes.portal.clients.client.record.$buildPath({
          params: { clientId },
        }),
      );
      closeSuggestions();
    },
    [closeSuggestions, navigate],
  );

  const handleUserClick = useCallback(
    (userId?: string) => {
      if (!userId) return;
      navigate(routes.portal.users.user.$buildPath({ params: { userId } }));
      closeSuggestions();
    },
    [closeSuggestions, navigate],
  );

  const handleEmployeeClick = useCallback(
    (clientId: string, clientEmployeeId?: string) => {
      if (!clientEmployeeId) return;
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
      );
      closeSuggestions();
    },
    [closeSuggestions, navigate],
  );

  const handleApplicationClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.dashboard.$buildPath(
          {
            params: {
              clientId,
              applicationId,
            },
          },
        ),
      );
      closeSuggestions();
    },
    [closeSuggestions, navigate],
  );

  const handleCatalogResourceClick = useCallback(
    (searchTerm: string) => {
      navigate(
        routes.portal.administration.applications.$buildPath({
          searchParams: { search: searchTerm },
        }),
        { viewTransition: true },
      );
      closeSuggestions();
    },
    [closeSuggestions, navigate],
  );

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  const showScopeControl = !!routeClientId;
  const activeScopeLabel =
    scope === "this-client" ? "This client" : "All clients";

  return (
    <>
      <div className={classes.search} ref={(el) => setReference(el)}>
        <Icon icon="faMagnifyingGlass" size="sm" />
        <Input
          {...getReferenceProps({
            type: "text",
            onChange: handleSearch,
            onKeyDown: handleKeyDown,
            value: search,
            placeholder: "Search...",
            "aria-autocomplete": "list",
            // This ensures that the input doesn't preserve min-width on
            // small screens and wraps correctly when there isn't enough space.
            size: 1,
          })}
          transparent
        />
        {showScopeControl && (
          <div
            className={classes.scopeToggle}
            role="group"
            aria-label={`Search scope: ${activeScopeLabel}`}
          >
            <button
              type="button"
              className={classes.scopeOption}
              aria-pressed={scope === "this-client"}
              title="Search within the selected client"
              onMouseDown={handleScopeMouseDown}
              onClick={() => handleScopeChange("this-client")}
            >
              This client
            </button>
            <button
              type="button"
              className={classes.scopeOption}
              aria-pressed={scope === "all-clients"}
              title="Search across all clients"
              onMouseDown={handleScopeMouseDown}
              onClick={() => handleScopeChange("all-clients")}
            >
              All clients
            </button>
          </div>
        )}
      </div>
      <FloatingPortal>
        <GlobalSearchContext.Provider value={dropdownContext}>
          {isOpen && (
            <FloatingFocusManager
              context={context}
              modal={false}
              order={["reference", "floating"]}
              returnFocus={false}
            >
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 1001 }}
                className={classes.floating}
                {...getFloatingProps()}
              >
                <Card>
                  <ScrollBox stretch>
                    <div className={classes.list}>
                      <FloatingList
                        elementsRef={elementsRef}
                        labelsRef={labelsRef}
                      >
                        <Box direction="column" gap={16} padding="12px">
                          {isClientsFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faSuitcase" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Tenants
                                </Text>
                              </Box>
                              {results.clients?.map((client) => {
                                if (!client?.id) return null;
                                return (
                                  <GlobalSearchOption
                                    key={client.id}
                                    id={`client:${client.id}`}
                                    label={client.name || client.id}
                                    onSelect={() =>
                                      handleClientClick(client.id)
                                    }
                                  >
                                    <div className={classes.truncatedText}>
                                      <Resource
                                        name={client.name}
                                        domain={client.domain}
                                      />
                                    </div>
                                  </GlobalSearchOption>
                                );
                              })}
                            </Box>
                          )}

                          {isUsersFound && (
                            <Box direction="column" gap={12} stretch>
                              <Box gap={6}>
                                <Icon icon="faUser" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Staff members
                                </Text>
                              </Box>
                              {results.users?.map((user) => {
                                if (!user?.id) return null;
                                return (
                                  <GlobalSearchOption
                                    key={user.id}
                                    id={`user:${user.id}`}
                                    label={user.name || user.email || user.id}
                                    onSelect={() => handleUserClick(user.id)}
                                  >
                                    <div className={classes.truncatedText}>
                                      <User
                                        name={user.name}
                                        email={user.email}
                                      />
                                    </div>
                                  </GlobalSearchOption>
                                );
                              })}
                            </Box>
                          )}

                          {isEmployeesFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faUserTie" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Users
                                </Text>
                              </Box>
                              {employeesGroupedByTenant.map((tenantGroup) => (
                                <Box
                                  key={tenantGroup.clientId}
                                  direction="column"
                                  gap={8}
                                  fullStretch
                                >
                                  <Box gap={6} padding="8px 0 0 12px">
                                    <Text color="textSecondary">
                                      <Icon icon="faSuitcase" size="sm" />
                                    </Text>
                                    <Text
                                      size={12}
                                      weight={700}
                                      color="textSecondary"
                                      uppercase
                                    >
                                      {tenantGroup.clientName}
                                    </Text>
                                  </Box>
                                  <Box
                                    direction="column"
                                    gap={8}
                                    padding="0 0 0 24px"
                                    fullStretch
                                  >
                                    {tenantGroup.employees.map((employee) => (
                                      <GlobalSearchOption
                                        key={employee.employeeId}
                                        id={`employee:${employee.employeeId}`}
                                        label={
                                          employee.name ||
                                          employee.email ||
                                          employee.employeeId
                                        }
                                        onSelect={() =>
                                          handleEmployeeClick(
                                            tenantGroup.clientId,
                                            employee.employeeId,
                                          )
                                        }
                                      >
                                        <div className={classes.truncatedText}>
                                          <User
                                            name={employee.name}
                                            email={employee.email}
                                          />
                                        </div>
                                      </GlobalSearchOption>
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {isCatalogFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faBookAtlas" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Application catalog
                                </Text>
                              </Box>
                              {results.catalogResources?.map((resource) => {
                                if (!resource?.id) return null;
                                const searchTerm =
                                  resource.productName || debouncedSearch;
                                return (
                                  <GlobalSearchOption
                                    key={resource.id}
                                    id={`catalog:${resource.id}`}
                                    label={
                                      resource.productName ||
                                      resource.productDomainName ||
                                      resource.id
                                    }
                                    onSelect={() =>
                                      handleCatalogResourceClick(searchTerm)
                                    }
                                  >
                                    <div className={classes.truncatedText}>
                                      <Resource
                                        name={
                                          resource.productName ?? undefined
                                        }
                                        subtext={
                                          resource.vendorName ?? undefined
                                        }
                                        domain={
                                          resource.productDomainName ??
                                          undefined
                                        }
                                        signedLogoUrl={
                                          resource.signedLogoUrl ?? undefined
                                        }
                                      />
                                    </div>
                                  </GlobalSearchOption>
                                );
                              })}
                            </Box>
                          )}

                          {isApplicationsFound && (
                            <Box
                              direction="column"
                              gap={12}
                              stretch
                              fullStretch
                            >
                              <Box gap={6}>
                                <Icon icon="faShapes" size="sm" />
                                <Text
                                  size={12}
                                  weight={700}
                                  color="textSecondary"
                                  uppercase
                                >
                                  Discovered applications
                                </Text>
                              </Box>
                              {applicationsGroupedByTenant.map(
                                (tenantGroup) => (
                                  <Box
                                    key={tenantGroup.clientId}
                                    direction="column"
                                    gap={8}
                                    fullStretch
                                  >
                                    <Box gap={6} padding="8px 0 0 12px">
                                      <Text color="textSecondary">
                                        <Icon icon="faSuitcase" size="sm" />
                                      </Text>
                                      <Text
                                        size={12}
                                        weight={700}
                                        color="textSecondary"
                                        uppercase
                                      >
                                        {tenantGroup.clientName}
                                      </Text>
                                    </Box>
                                    <Box
                                      direction="column"
                                      gap={8}
                                      padding="0 0 0 24px"
                                      fullStretch
                                    >
                                      {tenantGroup.apps.map((app) => (
                                        <GlobalSearchOption
                                          key={app.clientAppId}
                                          id={`application:${app.clientAppId}`}
                                          label={
                                            app.productName ||
                                            app.vendorName ||
                                            app.clientAppId
                                          }
                                          onSelect={() =>
                                            handleApplicationClick(
                                              tenantGroup.clientId,
                                              app.clientAppId,
                                            )
                                          }
                                        >
                                          <div className={classes.truncatedText}>
                                            <Resource
                                              name={app.vendorName}
                                              subtext={app.productName}
                                              domain={
                                                app.vendorDomainName ??
                                                app.productDomainName
                                              }
                                              signedLogoUrl={app.signedLogoUrl}
                                            />
                                          </div>
                                        </GlobalSearchOption>
                                      ))}
                                    </Box>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}

                          {isEmpty && !!debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                No results found
                              </Text>
                            </Box>
                          )}

                          {isEmpty && !debouncedSearch && (
                            <Box direction="column" gap={8}>
                              <Text
                                size={12}
                                weight={600}
                                color="textSecondary"
                              >
                                Search for anything...
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </FloatingList>
                    </div>
                  </ScrollBox>
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </GlobalSearchContext.Provider>
      </FloatingPortal>
    </>
  );
};
